import {useConfig} from '@aboutyou/config';
import {GrpcResponseError} from '@aboutyou/data-fetcher';
import {useIsFeatureActive} from '@aboutyou/feature-flags';
import {DeliveryItem} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/basket/v1/delivery.types';
import {useTranslation} from '@aboutyou/i18n';
import {
  useTrackFeature,
  useTrackFeatureInViewImpression,
  useTrackNewBasketAction,
  useTrackOriginClick,
  useTrackProductClick,
} from '@aboutyou/tracking';
import {HeightTransition, Slide} from '@aboutyou/transitions';
import {
  COLORS,
  GenericIconName,
  Icon,
  OriginalPriceWrapper,
  ProductCard,
  ProductImageType,
  QuantityChangeButton,
  RichText,
  UnstyledButton,
  tabletAndUpLinaria,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';
import {BadgeList} from '../../../../features/BadgeList/BadgeList';
import {
  useBadgesList,
  useBadgesListKeys,
} from '../../../../features/BadgeList/useBadgesList';
import {COUPON_PROMOTION_ID_QUERY_KEY} from '../../../../features/Coupons/useCouponPromotionIdFromQueryParams';
import {useAddToBasketErrorToast} from '../../../../features/SizeSelectionFlyout/hooks/useAddToBasketErrorToast';
import {useQuantityChangeHandler} from '../../hooks/useQuantityChangeHandler';
import {DeliveriesProps} from '../Deliveries';
import {
  ITEM_ANIMATION_DELAY,
  useBasketDeliveryOptionalRemovedState,
} from '../hooks/useBasketDeliveryOptionalRemovedState';
import {DeleteWithUndo} from './DeleteWithUndo/DeleteWithUndo';
import {WishlistButton} from './WishlistButton';

interface ProductTileInteractiveProps {
  item: DeliveryItem;
  preferredProductImageType?: ProductImageType;
  onLegalInfoClick: (options: {size: number; productId: number}) => void;
  shouldCheckForPriceRise?: boolean;
}

export const BasketProductCard: React.FC<
  ProductTileInteractiveProps & DeliveriesProps
> = ({
  item,
  preferredProductImageType,
  onBasketQuantityChange,
  onBasketItemRemovedSuccessfully,
  onLegalInfoClick,
  shouldCheckForPriceRise,
}) => {
  const {
    product,
    size,
    itemKey,
    brand,
    quantity,
    availableQuantity,
    sizeLabel,
    badges,
    lowStockHint,
    totalPrice,
    appliedCoinsPromotionId,
    unitPrice,
    lowestPriorPriceHint,
    maximumOrderQuantity,
    totalPriceV2,
  } = item;
  const {isActive} = useIsFeatureActive('ab_price_display_overhaul_v2');
  const {isActive: isNewTabActive} = useIsFeatureActive('ab_adp_new_tab');
  const {device} = useConfig();
  const {t} = useTranslation();
  const showAddToBasketErrorToast = useAddToBasketErrorToast();

  const {
    isItemMarkedForDeletion,
    isItemDeleted,
    onDeleteIconClick,
    deleteItemWithDelay,
    cancelDeletion,
    deleteItem,
  } = useBasketDeliveryOptionalRemovedState({
    itemKey,
    onDelete: onBasketItemRemovedSuccessfully,
  });

  const quantityChangeHandler = useQuantityChangeHandler(item.itemKey);

  const trackBasketAction = useTrackNewBasketAction();
  const getBadgesConfig = {
    badges,
    sale: null,
    campaignSale: null,
    useDefaultLabels: true,
    showPriceOverhaulV2SaleLabel: false,
  };
  const badgesList = useBadgesList(getBadgesConfig);
  const badgesListKeys = useBadgesListKeys(getBadgesConfig);

  const trackFeature = useTrackFeature();

  const onDeleteItem = () => {
    trackBasketAction({
      type: 'removed-from-basket',
      itemTrackingData: item.itemTrackingData,
      brand: item.brand,
      sizeLabel: item.sizeLabel || '',
      badges: badgesListKeys,
    });

    onDeleteIconClick();
  };

  const onQuantityChange = (updatedQuantity: number) => {
    trackFeature({
      eventType: 'submit',
      label: 'basket_product_menu|quantity_selection',
    });

    quantityChangeHandler(updatedQuantity)
      .then(response => {
        onBasketQuantityChange?.({
          isMovReached:
            response.sections.movSection?.freeDeliverySection.type?.$case ===
            'infoMessage',
        });

        // track add_to_cart / remove_from_cart events
        trackBasketAction({
          type:
            updatedQuantity > quantity
              ? 'add-to-basket'
              : 'removed-from-basket',
          itemTrackingData: {...item.itemTrackingData, quantity: 1},
          brand: item.brand,
          sizeLabel: item.sizeLabel || '',
          badges: badgesListKeys,
        });
      })
      .catch(err => {
        const {code} = err as GrpcResponseError;

        showAddToBasketErrorToast({
          failed: true,
          code,
        });
      });
  };

  const trackProductClick = useTrackProductClick(badgesListKeys);
  const trackOriginClick = useTrackOriginClick();
  const productTileTrackingData = {
    product: item.product,
    brand: item.brand,
  };

  const handleCancelDeletion = () => {
    cancelDeletion();

    trackFeature({
      eventType: 'click',
      label: `cancel_remove_from_cart|${item.product.id}`,
    });
  };

  const showMerchantInfo = item.sellingMerchant;

  const productPath = item.appliedCouponPromotionId
    ? `${product.path}?${COUPON_PROMOTION_ID_QUERY_KEY}=${item.appliedCouponPromotionId}`
    : product.path;

  const {ref} = useTrackFeatureInViewImpression<HTMLDivElement>({
    ...item.deliveryEstimationTracker,
  });

  return (
    <div ref={ref}>
      <StyledHeightTransition
        mounted={!isItemDeleted}
        duration={ITEM_ANIMATION_DELAY}
        preventAnimationOnMount
      >
        <div>
          <StyledProductCard
            openInNewTab={isNewTabActive && device === 'desktop'}
            product={{
              path: productPath,
              image:
                preferredProductImageType &&
                preferredProductImageType === 'model'
                  ? product.primaryModelImage
                  : product.primaryBustImage,
              name: product.name,
              colorLabel: product.colorLabel,
              brand: brand.name,
              sizeLabel: sizeLabel || '',
              lowStockHint,
              id: product.id,
            }}
            badges={
              <BadgeList
                badgesList={badgesList}
                increaseRightSideBorderRadius
              />
            }
            quantity={{
              quantity,
              maxQuantity: availableQuantity,
            }}
            quantityBlock={
              <QuantityChangeButton
                onChange={onQuantityChange}
                value={quantity}
                max={Math.min(
                  availableQuantity,
                  maximumOrderQuantity ?? Infinity,
                )}
                min={1}
              />
            }
            totalPrice={isActive ? totalPriceV2 : totalPrice}
            unitPrice={unitPrice}
            lowestPriorPrice={lowestPriorPriceHint?.label}
            overflowHiddenLPL30D={isActive && device === 'mobile'}
            wishlistButton={
              <WishlistButton
                brand={brand}
                product={product}
                badgesListKeys={badgesListKeys}
              />
            }
            additionalBadge={undefined}
            deleteBlock={
              <DeleteItemButton
                data-testid="removeProduct"
                onClick={onDeleteItem}
                aria-label={t('remove product')}
              >
                <AnimatedIcon
                  size="S"
                  asset={GenericIconName.Trash}
                  fill={COLORS.Grey1}
                />
              </DeleteItemButton>
            }
            trackProductClick={() =>
              trackProductClick(productTileTrackingData, 1)
            }
            trackProductsOriginClick={() => trackOriginClick('basket_products')}
            sellingMerchant={
              showMerchantInfo && (
                <SellingMerchantWrapper data-testid="soldByMerchantInfo">
                  <RichText size="px10" color="Grey0">
                    {item.sellingMerchant!.soldBy.text}
                  </RichText>
                  <UnstyledButton
                    onClick={() => {
                      onLegalInfoClick({
                        size: size.id,
                        productId: product.id,
                      });
                    }}
                    aria-label={t('open merchant info popup')}
                  >
                    <InfoIcon
                      asset={GenericIconName.Info}
                      fill={COLORS.Grey0}
                    />
                  </UnstyledButton>
                </SellingMerchantWrapper>
              )
            }
            icon={
              appliedCoinsPromotionId && (
                <CoinsIcon asset={GenericIconName.CoinsDoubleColor} size="S" />
              )
            }
          />
          <Slide
            mounted={isItemMarkedForDeletion}
            direction="left"
            duration={250}
          >
            <DeleteWithUndo
              key={`${itemKey}-${isItemMarkedForDeletion}`}
              itemKey={itemKey}
              deleteItemWithDelay={deleteItemWithDelay}
              deleteItem={deleteItem}
              onUndoClick={handleCancelDeletion}
              shouldCheckForPriceRise={shouldCheckForPriceRise}
            />
          </Slide>
        </div>
      </StyledHeightTransition>
    </div>
  );
};

const SellingMerchantWrapper = styled.div`
  display: flex;
  gap: 0.25rem;
  align-items: center;
`;

const InfoIcon = styled(Icon)`
  height: 0.75rem;
  width: 0.75rem;
`;

const StyledProductCard = styled(ProductCard)`
  padding-inline: 16px;
  ${tabletAndUpLinaria(`
    padding-inline: 24px;
  `)}

  ${OriginalPriceWrapper} {
    justify-content: flex-end;
  }
`;

const StyledHeightTransition = styled(HeightTransition)`
  position: relative;
  overflow: hidden;
`;

export const AnimatedIcon = styled(Icon)`
  transition: fill 300ms ease;

  button:hover & {
    fill: ${COLORS.Black};
  }
`;

export const DeleteItemButton = styled(UnstyledButton)`
  display: flex;
  align-items: flex-start;
  padding: 0 5px;
  margin-top: -4px;
`;

const CoinsIcon = styled(Icon)`
  background-color: ${COLORS.White};
  position: absolute;
  top: 6px;
  left: 6px;
  z-index: 1;
  padding: 4px;
  border-radius: 50%;
`;
